import z from "zod";
import { ClientOrderStatusEnum, ClientOrderTypeEnum, LimitTypeEnum, RentTypeEnum } from "../domain/entities/ClientOrdersEntity";
import { ClientOrdersApiRepository } from "./ClientOrdersApiRepository";
import { Badge } from "@/components/atoms/Badge";

export const ClientOrderTypeOptions = [
    { label: ClientOrderTypeEnum[ClientOrderTypeEnum.COMPRA], value: ClientOrderTypeEnum.COMPRA.toString() },
    { label: ClientOrderTypeEnum[ClientOrderTypeEnum.VENTA], value: ClientOrderTypeEnum.VENTA.toString() },
];

export const ClientOrderStatusOptions = [
    { label: ClientOrderStatusEnum[ClientOrderStatusEnum.REGISTRO], value: ClientOrderStatusEnum.REGISTRO.toString() },
    { label: ClientOrderStatusEnum[ClientOrderStatusEnum.COMPLEMENTACION], value: ClientOrderStatusEnum.COMPLEMENTACION.toString() },
    { label: ClientOrderStatusEnum[ClientOrderStatusEnum.FIN], value: ClientOrderStatusEnum.FIN.toString() },
];

export const clientVariantMap: Record<ClientOrderStatusEnum, "info" | "warning" | "success"> = {
    [ClientOrderStatusEnum.REGISTRO]: "info",
    [ClientOrderStatusEnum.COMPLEMENTACION]: "warning",
    [ClientOrderStatusEnum.FIN]: "success",
};

export const clientOrderColumns = [
    {
        header: "Estatus",
        accessor: "status",
        render: (value: string) => {
            const variant = clientVariantMap[value as unknown as ClientOrderStatusEnum] ?? "info";
            return <Badge variant={variant}>{value.charAt(0)}</Badge>;
        },
        filterValue: (value: string) => value
    },
    { header: "Cliente", accessor: "client.nombre" },
    {
        header: "Tipo Orden",
        accessor: "orderType",
        render: (value: string) => {
            const variant = value === ClientOrderTypeEnum.COMPRA ? "darkInfo" : "darkError";
            return (
                <Badge variant={variant} className="w-full">
                    {`${value}`}
                </Badge>
            );
        }
    },
    {
        header: "Tipo Renta",
        accessor: "rentType",
        render: (value: string) => {
            const variant = value === RentTypeEnum.RF ? "success" : "warning";
            return (
                <Badge variant={variant} className="w-full">
                    {rentTypeLabels[value as RentTypeEnum]}
                </Badge>
            );
        }
    },
    {
        header: "Tipo Límite",
        accessor: "limitType",
        render: (value: string, row: { rentType: RentTypeEnum; }) => {
            if (row.rentType === RentTypeEnum.RV) {
                const variant = value === LimitTypeEnum.LIMITE ? "secondary" : "primary";
                return (
                    <Badge variant={variant} className="w-full">
                        {limitTypeLabels[value as LimitTypeEnum]}
                    </Badge>
                );
            }
        }
    },
    { header: "Instrumento", accessor: "instrument.mnemonic" },
    {
        header: "Cantidad",
        accessor: "quantity",
        render: (value: number) =>
            new Intl.NumberFormat("es-ES", {
                style: "decimal",
                minimumFractionDigits: 2,
            }).format(value),
    },
    {
        header: "Tasa",
        accessor: "rate",
        render: (value: number, row: { rentType: RentTypeEnum; }) => {

            if (row.rentType === RentTypeEnum.RF) {
                return new Intl.NumberFormat("es-ES", {
                    style: "percent",
                    minimumFractionDigits: 2,
                }).format(value / 100);
            }
        }
    },
    {
        header: "Precio Límite",
        accessor: "limitPrice",
        render: (value: number, row: { rentType: RentTypeEnum; limitType: LimitTypeEnum }) => {

            if (row.rentType === RentTypeEnum.RV && row.limitType === LimitTypeEnum.LIMITE) {
                return `$${value.toLocaleString("es-CO",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}`
            }
        }
    },
    {
        header: "Valor",
        accessor: "value",
        render: (value: number, row: { rentType: RentTypeEnum; limitType: LimitTypeEnum }) => {

            if (row.rentType === RentTypeEnum.RV && row.limitType === LimitTypeEnum.LIMITE) {
                return `$${value.toLocaleString("es-CO",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}`
            }
        }
    },
    {
        header: "Creado",
        accessor: "createdAt",
        render: (value: Date) => value.toDateString(),
    },
];

export const clientOrderTypeOptions = [
    { label: ClientOrderTypeEnum[ClientOrderTypeEnum.COMPRA], value: ClientOrderTypeEnum.COMPRA.toString() },
    { label: ClientOrderTypeEnum[ClientOrderTypeEnum.VENTA], value: ClientOrderTypeEnum.VENTA.toString() },
];

// Identification type options
export const identificationTypeOptions = [
    { label: "Cédula de Ciudadanía", value: "CC" },
    { label: "NIT", value: "NI" },
    { label: "Cédula de Extranjería", value: "CE" },
    { label: "Pasaporte", value: "PA" },
];

// Rent type label mapping
export const rentTypeLabels: Record<RentTypeEnum, string> = {
    [RentTypeEnum.RF]: "RENTA FIJA",
    [RentTypeEnum.RV]: "RENTA VARIABLE",
};

// Rent type label mapping
export const limitTypeLabels: Record<LimitTypeEnum, string> = {
    [LimitTypeEnum.LIMITE]: "LIMITE",
    [LimitTypeEnum.MERCADO]: "MERCADO",
};

// Rent type options
export const rentTypeOptions = [
    { label: rentTypeLabels[RentTypeEnum.RF], value: RentTypeEnum.RF },
    { label: rentTypeLabels[RentTypeEnum.RV], value: RentTypeEnum.RV },
];

export const clientOrderSchema = z
    .object({
        id: z.string().optional(),
        client: z.string().min(1, "El cliente es obligatorio"),
        clientIdentification: z.string().min(1, "La identificación del cliente es obligatoria"),
        clientIdentificationType: z.string().min(1, "El tipo de identificación es obligatorio"),
        orderType: z.boolean(),
        rentType: z.string().min(1, "El tipo de renta es obligatorio"),
        instrument: z.string().min(1, "El instrumento es obligatorio"),
        quantity: z.number({ invalid_type_error: "La cantidad debe ser un número" })
            .min(1, "Debe ser mayor a 0"),
        // limitType comes from the UI switch (MERCADO | LIMITE)
        limitType: z.boolean(),
        // These are conditionally required depending on rentType/limitType
        limitPrice: z.number({ invalid_type_error: "El límite debe ser un número" }).optional(),
        rate: z.number({ invalid_type_error: "La tasa debe ser un número" }).optional(),
        value: z.number({ invalid_type_error: "El valor debe ser un número" }).optional(),
        confirmNumber:
            z.string()
                .min(1, "El número de confirmación es obligatorio")
                .optional()
                .or(z.literal("")),
        status: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        const isRF = data.rentType === RentTypeEnum.RF;
        const isRV = data.rentType === RentTypeEnum.RV;
        const isMercado = data.limitType;
        const isLimite = !data.limitType;
        // Path 1: RF -> require rate and value
        if (isRF) {
            if (data.rate == null || data.rate <= 0 || data.rate > 100) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["rate"],
                    message: "La tasa es obligatoria y debe estar entre 1 y 100",
                });
            }

            if (data.value == null || data.value <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["value"],
                    message: "El valor es obligatorio y debe ser mayor a 0",
                });
            }
        }

        // Path 2: RV + MERCADO -> only quantity is required (already enforced above)
        if (isRV && isMercado) {
            // No extra numeric requirements; allow rate/value/limitPrice to be empty or 0
            return;
        }

        // Path 3: RV + LIMITE -> require limitPrice and value
        if (isRV && isLimite) {
            if (data.limitPrice == null || data.limitPrice <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["limitPrice"],
                    message: "El precio límite es obligatorio y debe ser mayor a 0",
                });
            }

            if (data.value == null || data.value <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["value"],
                    message: "El valor es obligatorio y debe ser mayor a 0",
                });
            }
        }
    });

export type ClientOrderFormData = z.infer<typeof clientOrderSchema>;

export const clientOrderRepository = new ClientOrdersApiRepository();

export const clientOrderKeys = {
    all: ['clientOrders'] as const,
    details: (id: string) => [...clientOrderKeys.all, id] as const,
    allTypes: ['clientOrderTypes'] as const,
    allClients: ['clients'] as const,
    allInstruments: ['clientInstrument'] as const,
    allRates: ['clientRate'] as const,
};
